import { create } from 'zustand';

/**
 * rightEdgePanelStore — tiny cross-widget signal for the right gutter.
 *
 * `pendingTasksOpen` is true while a right-edge OPEN panel (the doctor's
 * Pending Tasks list, rendered via RoleTaskBadge) occupies the right
 * gutter. The per-visit ProcedureWidget disc reads this so it can step
 * out of the way (hide its collapsed circle) instead of painting on top
 * of the panel and occluding the cost text.
 *
 * Lives in `src/stores/` (not `src/widgets/internals/`) because both the
 * publisher (components/…/RoleTaskBadge) and the consumer (widgets/impl/
 * ProcedureWidget) need it, and widgetRouterStore is ESLint-fenced to
 * `src/widgets/` only.
 */
const useRightEdgePanelStore = create((set) => ({
  // True while ANY right-edge panel is open (read by ProcedureWidget's disc).
  pendingTasksOpen: false,
  // Identity of the currently-open right-edge panel. Lets sibling badges
  // (e.g. receptionist's amber "Action pending" + purple "Confirm
  // Appointments") hide their collapsed bubble while another's panel is open,
  // so two right-edge badges never paint on top of each other.
  openPanelKey: null,
  setPendingTasksOpen: (v) => set({ pendingTasksOpen: !!v }),
  // key: stable per-badge id; isOpen: whether that badge's panel is open.
  setOpenPanel: (key, isOpen) => set((s) => {
    if (isOpen) return { openPanelKey: key, pendingTasksOpen: true };
    // Only the badge that owns the open slot may clear it (avoids a closing
    // sibling stomping the still-open one).
    if (s.openPanelKey === key) return { openPanelKey: null, pendingTasksOpen: false };
    return {};
  }),
}));

export default useRightEdgePanelStore;
